import type { OnboardingStepId, StepAction } from "../types";
import { motion } from "framer-motion";

type ButtonProps = {
  action: StepAction;
  isCurrentFieldInvalid: boolean;
  handleActionClick: (action: StepAction) => void | Promise<void>;
  stepId: OnboardingStepId;
  isLoading?: boolean;
  isDisabled?: boolean;
  className?: string;
};

const BackArrowIcon = () => {
  return (
    <svg
      width={16}
      height={16}
      className="fs-0"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M6.38016 3.95312L2.3335 7.99979L6.38016 12.0465"
        stroke="#FB5F66"
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.6668 8H2.44678"
        stroke="#FB5F66"
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const baseButtonClass =
  "flex items-center justify-center rounded-[12px] px-[16px] py-[12px] text-[13px] leading-[110%] transition-colors";

const buttonVariantClass = {
  back: "min-w-[76px] gap-[5px] rounded-[10px] border border-[#FB5F6680] bg-white text-[#FB5F66] hover:bg-[#fff5f6]",
  primary:
    "gap-[5px] bg-[#fb5f66] text-white hover:shadow-[0px_4px_20px_rgba(251,95,102,0.15)]",
  secondary:
    "gap-[5px] border border-[#d9d9d9] bg-white text-[#444] hover:bg-[#f7f7f7]",
};

const Button = ({
  action,
  isCurrentFieldInvalid,
  handleActionClick,
  stepId,
  isLoading = false,
  isDisabled: isDisabledByParent = false,
  className = "",
}: ButtonProps) => {
  const loadingLabel =
    action.id === "fetchWebsiteContent" ? "Fetching..." : "Creating...";
  const isBackButton = action.id === "back";
  const isDisabled =
    (action.id !== "back" && (isCurrentFieldInvalid || isDisabledByParent)) ||
    isLoading;
  const variantClass = isBackButton
    ? buttonVariantClass.back
    : action.variant === "primary"
      ? buttonVariantClass.primary
      : buttonVariantClass.secondary;
  const stateClass = isDisabled
    ? "cursor-not-allowed opacity-50"
    : "cursor-pointer opacity-100";

  return (
    <motion.button
      key={action.id}
      className={`${baseButtonClass} ${variantClass} ${stateClass} h-[38px] ${stepId === 1 ? "w-[150px] px-[16px] text-[14px] font-medium leading-normal" : ""} ${className}`}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      type="button"
      whileTap={isDisabled ? undefined : { scale: 0.985 }}
      animate={
        isLoading
          ? { opacity: [0.5, 0.72, 0.5] }
          : { opacity: isDisabled ? 0.5 : 1 }
      }
      transition={
        isLoading
          ? { duration: 1.1, repeat: Infinity, ease: "easeInOut" }
          : { duration: 0.16 }
      }
      onClick={() => {
        if (isDisabled) return;
        handleActionClick(action);
      }}
    >
      {isLoading ? (
        loadingLabel
      ) : (
        <>
          {isBackButton && <BackArrowIcon />}
          {action.label}
        </>
      )}
    </motion.button>
  );
};

export default Button;
